<template>
  <div
    class="form-input"
    :class="[$attrs.class, { '-error': hasError, '-disabled': disabled }]"
    :style="$attrs.style as any"
  >
    <input
      :id="name"
      v-bind="{ ...$attrs, class: null, style: null }"
      :type="type"
      :placeholder="placeholder || ' '"
      :required="required"
      :disabled="disabled"
      class="form-control text-md"
      :name="name"
      :value="model"
      @input="handleInput"
      @blur="validateForm"
    />
    <label class="label copy-regular" :for="name">{{ label }}</label>
    <div v-if="hasError" class="error-message copy-small">
      {{ error }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

defineOptions({
  inheritAttrs: false,
});

defineExpose({
  validateForm,
});

const props = defineProps<{
  label?: string;
  name?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  type?: string;
  disabled?: boolean;
  customValidation?: (value: string) => boolean;
}>();

const model = defineModel<string | null | undefined>({ required: true });
const hasError = ref<boolean>(false);

// Validation patterns
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/;
const urlPattern =
  /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;

function validateForm() {
  // Skip validation if disabled
  if (props.disabled) {
    return true;
  }

  const currentValue = model.value ?? '';
  let isValid = true;

  // Required validation
  if (props.required) {
    isValid = Boolean(currentValue && currentValue.trim() !== '');
  }

  if (props.customValidation) {
    isValid = props.customValidation(currentValue);
  }

  // Format validation (only if there's a value and a type specified)
  if (isValid && currentValue && currentValue.trim() !== '' && props.type) {
    switch (props.type) {
      case 'email':
        isValid = Boolean(emailPattern.test(currentValue));
        break;
      case 'tel':
        isValid = Boolean(phonePattern.test(currentValue));
        break;
      case 'url':
        isValid = Boolean(urlPattern.test(currentValue));
        break;
    }
  }

  hasError.value = !isValid;

  return isValid;
}

function handleInput(event: Event) {
  // Skip input handling if disabled
  if (props.disabled) {
    return;
  }

  const target = event.target as HTMLInputElement;
  model.value = target.value;

  // Clear error when the user starts typing
  if (hasError.value) {
    hasError.value = false;
  }
}
</script>

<style scoped lang="scss">
.form-input {
  --border-width-input: 1px;

  position: relative;

  &.-error {
    > .form-control {
      border-color: var(--color-error-red);
      color: var(--color-error-red);

      &:focus {
        color: var(--color-error-red);
        border-color: var(--color-error-red);
        outline-color: var(--color-error-red);

        ~ .label {
          color: var(--color-error-red);
        }
      }
    }

    > .label {
      color: var(--color-error-red);
    }
  }

  &.-disabled {
    > .form-control {
      background-color: #fafafa;
      border-color: #ccc;
      color: #666;
      cursor: not-allowed;
    }

    > .label {
      color: var(--color-gray-dark, #666);
      cursor: not-allowed;
    }
  }

  > .form-control {
    width: 100%;
    height: 3.4rem;
    border: var(--border-width-input) solid var(--color-black);
    border-radius: 0;
    padding: var(--spacing-3xs);
    color: var(--color-black);

    &::placeholder {
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &:focus {
      color: var(--base-color-brand);
      border-color: var(--base-color-brand);
      outline: 1px solid var(--base-color-brand);

      &::placeholder {
        opacity: 0.5;
      }
    }

    &:focus ~ .label,
    &:not(:placeholder-shown) ~ .label {
      background-color: var(--color-white);
      transform: scale(0.89) translateY(-50%) translateX(-0.25rem);
      padding: 0 0.25rem;
    }

    &:focus ~ .label {
      color: var(--base-color-brand);
    }
  }

  > .label {
    position: absolute;
    transform: translateY(50%);
    top: var(--border-width-input);
    left: calc(var(--spacing-3xs) + var(--border-width-input));
    pointer-events: none;
    transform-origin: 0 0;
    transition:
      background-color 0.1s ease-in-out,
      padding 0.1s ease-in-out,
      transform 0.1s ease-in-out;
  }

  > .error-message {
    color: var(--color-error-red);
    margin-top: var(--spacing-4xs);
  }
}
</style>
